'use client';

import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
  ColumnDef,
} from '@tanstack/react-table';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { apiService } from '../services/apiService';
import { Eye, X, Check, Copy, Shield, Lock, FileText, Search, ShieldCheck, Layers, Hash, CheckCircle2 } from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
//  Config
// ─────────────────────────────────────────────────────────────────────────────
const API_BASE_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' && window.location.port === '3000' ? `http://${window.location.hostname}:8000` : (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:8000'));

const getAuthHeaders = () => {
  const token = localStorage.getItem('fraudguard_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

// ─────────────────────────────────────────────────────────────────────────────
//  Formatters
// ─────────────────────────────────────────────────────────────────────────────
const formatCurrency = (v: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);

const formatNumber = (v: number) =>
  new Intl.NumberFormat('en-US').format(v);

const cleanSymbols = (text: string) => {
  if (!text) return '';
  return text
    .replace(/^[\s▪•\u25aa\u2022*`.\-+]+/, '') // Clean leading bullet/markdown indicators
    .replace(/[`*_~]+/g, '')                   // Clean inline backticks, asterisks, underscores, tildes
    .trim();
};

const PRESERVED_ACRONYMS = new Set([
  'AI', 'ML', 'API', 'UI', 'UX', 'ID', 'NPI', 'CPT', 'ICD', 'DRG',
  'HCPCS', 'LOS', 'YTD', 'MTD', 'QTD', 'USD', 'SAR', 'AED', 'EUR',
  'GBP', 'ROI', 'TAT', 'IP', 'OP', 'CO', 'HR', 'PPPY', 'QAR'
]);

const MINOR_WORDS = new Set(['and', 'or', 'of', 'for', 'in', 'to', 'with', 'by', 'at', 'on', 'the', 'a', 'an']);

const toTitleCase = (input: string | null | undefined): string => {
  if (!input) return '';
  return input.split('/').map(segment => {
    const clean = segment.replace(/[_-]+/g, ' ').trim();
    return clean.split(/\s+/).map((word, index) => {
      const upper = word.toUpperCase();
      if (PRESERVED_ACRONYMS.has(upper)) return upper;
      const lower = word.toLowerCase();
      if (index > 0 && MINOR_WORDS.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    }).join(' ');
  }).join(' / ');
};

// ─────────────────────────────────────────────────────────────────────────────
//  Types
// ─────────────────────────────────────────────────────────────────────────────
interface ProviderRow {
  provider_name?: string;
  provider?: string;
  visits: number;
  total_net: number;
  net_share: number;
}

interface KpiPagination {
  page: number;
  pageSize: number;
  total_count: number;
  total_pages: number;
}

interface KpisData {
  volume?: any;
  io_distribution?: any;
  avg_cost_per_visit?: any;
  inpatient_los?: any;
  cpt_kpis?: any;
  diagnosis_kpis?: any;
  financial_ratios?: any;
  high_cost_visits?: any;
  claims_per_provider?: ProviderRow[];
  pagination?: KpiPagination;
}

// ─────────────────────────────────────────────────────────────────────────────
//  API fetchers
// ─────────────────────────────────────────────────────────────────────────────

// Charts + KPI cards — only needs page/pageSize (no sort/search needed for charts)
const fetchKpis = async (page: number, pageSize: number): Promise<{ kpis: KpisData }> => {
  const url = `${API_BASE_URL}/api/provider/kpi?page=${page}&pageSize=${pageSize}`;
  const res = await fetch(url, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Error ${res.status}: ${res.statusText}`);
  return res.json();
};

// Provider table — supports page, pageSize, sort, order, search
const fetchProviderTable = async (params: {
  page: number;
  pageSize: number;
  sort: string;
  order: 'asc' | 'desc';
  search: string;
}): Promise<{ providers: ProviderRow[]; pagination: KpiPagination }> => {
  const backendSortMap: Record<string, string> = {
    provider_name: 'name',
    visits: 'claims',
    total_net: 'totalAmount',
    net_share: 'percentage'
  };
  const backendSort = backendSortMap[params.sort] || 'claims';

  const q = new URLSearchParams({
    page:     String(params.page),
    pageSize: String(params.pageSize),
    sort:     backendSort,
    order:    params.order,
    ...(params.search && { search: params.search }),
  });
  const res = await fetch(`${API_BASE_URL}/api/dashboard/providers?${q}`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error(`Error ${res.status}: ${res.statusText}`);
  const data = await res.json();

  // Map backend keys to expected frontend ProviderRow keys
  const mappedProviders = (data.providers || []).map((p: any) => ({
    provider_name: p.name,
    visits: p.claims,
    total_net: p.totalAmount,
    net_share: p.percentage / 100
  }));

  return {
    providers: mappedProviders,
    pagination: data.pagination
  };
};

// ─────────────────────────────────────────────────────────────────────────────
//  Colors
// ─────────────────────────────────────────────────────────────────────────────
const IO_COLORS = { I: '#6366f1', O: '#0ea5e9' };
const CPT_COLOR = '#0ea5e9';
const ICD_COLOR = '#a855f7';

// ─────────────────────────────────────────────────────────────────────────────
//  Shared UI
// ─────────────────────────────────────────────────────────────────────────────
const KPICard: React.FC<{
  label: string; value: string; sub?: string;
  color: string; bg: string; gradient: string; glow: string; iconPath: string;
}> = ({ label, value, sub, color, bg, gradient, glow, iconPath }) => (
  <div style={{ background: '#fff', borderRadius: '18px', border: '1px solid #e2e8f0', padding: '22px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
      <div style={{ width: '44px', height: '44px', background: gradient, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 14px ${glow}` }}>
        <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d={iconPath} />
        </svg>
      </div>
      <span style={{ padding: '3px 10px', background: bg, color, fontSize: '10px', fontWeight: 700, borderRadius: '999px', border: `1px solid ${color}40` }}>KPI</span>
    </div>
    <p style={{ fontSize: '11px', color: '#64748b', fontWeight: 500, margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{label}</p>
    <p style={{ fontSize: '26px', fontWeight: 800, color: '#0f172a', margin: '0 0 6px', letterSpacing: '-0.5px' }}>{value}</p>
    {sub && <span style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8' }}>{sub}</span>}
  </div>
);

const Loading: React.FC = () => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', padding: '80px 20px' }}>
    <svg style={{ animation: 'spin 1s linear infinite' }} width="32" height="32" fill="none" viewBox="0 0 24 24">
      <circle cx="12" cy="12" r="10" stroke="#e2e8f0" strokeWidth="3" />
      <path d="M4 12a8 8 0 018-8" stroke="#0ea5e9" strokeWidth="3" strokeLinecap="round" />
    </svg>
    <p style={{ fontSize: '13px', color: '#64748b', margin: '12px 0 0' }}>Loading analysis...</p>
  </div>
);

const EmptyState: React.FC = () => (
  <div style={{ textAlign: 'center', padding: '80px 20px' }}>
    <div style={{ width: '64px', height: '64px', background: '#f1f5f9', borderRadius: '18px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
      <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="#cbd5e1" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    </div>
    <p style={{ fontSize: '15px', fontWeight: 600, color: '#64748b', margin: '0 0 6px' }}>No Data Available</p>
    <p style={{ fontSize: '13px', color: '#94a3b8', margin: 0 }}>Please upload claims data first to view analytics.</p>
  </div>
);

const SortIcon: React.FC<{ direction: 'asc' | 'desc' | false }> = ({ direction }) => (
  <span style={{ display: 'inline-flex', flexDirection: 'column', marginLeft: '4px', gap: '1px' }}>
    <svg style={{ width: '10px', height: '10px', color: direction === 'asc' ? '#6366f1' : '#cbd5e1' }} fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 5l7 7H5l7-7z" />
    </svg>
    <svg style={{ width: '10px', height: '10px', color: direction === 'desc' ? '#6366f1' : '#cbd5e1' }} fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 19l-7-7h14l-7 7z" />
    </svg>
  </span>
);

// ─────────────────────────────────────────────────────────────────────────────
//  Provider table columns
// ─────────────────────────────────────────────────────────────────────────────
const columnHelper = createColumnHelper<ProviderRow>();

const providerColumns: ColumnDef<ProviderRow, any>[] = [
  columnHelper.display({
    id: 'rank',
    header: '#',
    enableSorting: false,
    cell: ({ row }) => {
      const i = row.index;
      return (
        <span style={{
          width: '24px', height: '24px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: '6px', fontSize: '11px', fontWeight: 700,
          background: i < 3 ? 'linear-gradient(135deg, #6366f1, #818cf8)' : '#f1f5f9',
          color: i < 3 ? 'white' : '#64748b',
        }}>
          {i + 1}
        </span>
      );
    },
  }),
  columnHelper.accessor(row => row.provider_name || row.provider || '-', {
    id: 'provider_name',
    header: 'Provider Name',
    enableSorting: true,
    cell: info => (
      <span style={{ fontWeight: 600, color: '#0f172a', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
        {info.getValue()}
      </span>
    ),
  }),
  columnHelper.accessor('visits', {
    id: 'visits',
    header: 'Visits',
    enableSorting: true,
    cell: info => (
      <span style={{ color: '#334155', fontWeight: 500 }}>{formatNumber(info.getValue())}</span>
    ),
  }),
  columnHelper.accessor('total_net', {
    id: 'total_net',
    header: 'Total Net',
    enableSorting: true,
    cell: info => (
      <span style={{ color: '#334155', fontWeight: 500 }}>{formatCurrency(info.getValue())}</span>
    ),
  }),
  columnHelper.accessor('net_share', {
    id: 'net_share',
    header: 'Net Share %',
    enableSorting: true,
    cell: info => {
      const pct = (info.getValue() * 100) || 0;
      return (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
          <div style={{ width: '80px', height: '6px', background: '#e2e8f0', borderRadius: '999px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: 'linear-gradient(90deg, #6366f1, #818cf8)', borderRadius: '999px' }} />
          </div>
          <span style={{ fontSize: '12px', fontWeight: 700, color: '#6366f1', minWidth: '40px', textAlign: 'right' }}>
            {pct.toFixed(1)}%
          </span>
        </div>
      );
    },
  }),
];

// ─────────────────────────────────────────────────────────────────────────────
//  Main Component
// ─────────────────────────────────────────────────────────────────────────────
interface CaseServiceDistributionProps {
  forcePolicyView?: boolean;
}

const CaseServiceDistribution: React.FC<CaseServiceDistributionProps> = ({ forcePolicyView }) => {

  // ── Charts state (page 1 only — charts don't paginate) ──────────────────
  const { data: kpiData, isLoading: kpiLoading, isError: kpiError, error: kpiErr } = useQuery({
    queryKey: ['kpi-charts'],
    queryFn: () => fetchKpis(1, 10),
    staleTime: 5 * 60 * 1000,
  });

  const kpis = kpiData?.kpis;

  const isPolicyBenefits = forcePolicyView || kpis?.cpt_kpis?.top_by_lines?.some((c: any) => c.cpt === 'BENEFIT') || false;
  const [filterType, setFilterType] = useState<'all' | 'inpatient' | 'outpatient'>('all');
  const [benefitPage, setBenefitPage] = useState(1);

  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [selectedClause, setSelectedClause] = useState<any | null>(null);
  const [copiedClause, setCopiedClause] = useState<boolean>(false);

  // Close modal on Escape key press (WCAG UX)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && selectedClause) {
        setSelectedClause(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedClause]);

  // 1. Fetch list of all policies
  const { data: policiesList } = useQuery({
    queryKey: ['policies-list'],
    queryFn: () => apiService.getPolicies(),
    enabled: isPolicyBenefits,
  });

  // 1b. Fallback: Fetch claims if policiesList is empty
  const hasPolicies = policiesList && policiesList.length > 0;
  const { data: claimsData } = useQuery({
    queryKey: ['policy-claims-fallback'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE_URL}/api/claims/filter?pageSize=100`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch policy benefits from claims');
      return res.json();
    },
    enabled: isPolicyBenefits && !hasPolicies,
  });

  // 2. Select default plan when policies load (ALL_POLICIES if multiple plans exist)
  useEffect(() => {
    if (policiesList && policiesList.length > 0 && !selectedPlan) {
      if (policiesList.length > 1) {
        setSelectedPlan('ALL_POLICIES');
      } else {
        setSelectedPlan(policiesList[0].plan_name);
      }
    }
  }, [policiesList, selectedPlan]);

  // 3. Fetch details for the selected plan
  const { data: policyDetails, isLoading: loadingDetails, isFetching: fetchingDetails } = useQuery({
    queryKey: ['policy-details', selectedPlan],
    queryFn: () => apiService.getPolicyDetails(selectedPlan),
    enabled: isPolicyBenefits && !!selectedPlan && hasPolicies,
    placeholderData: keepPreviousData,
  });

  // ── Provider table state ─────────────────────────────────────────────────
  const [tablePage, setTablePage]     = useState(1);
  const [tablePageSize]               = useState(10);
  const [sorting, setSorting]         = useState<SortingState>([{ id: 'visits', desc: true }]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch]           = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const exportPolicyCSV = () => {
    const dataToExport = hasPolicies ? (policyDetails?.benefits || []) : (claimsData?.results || []);
    if (!dataToExport.length) return;
    
    const headers = [
      '#', 'Policy Number', 'Plan Name', 'Network Type',
      'Start Date', 'End Date', 'Status',
      'Policy Benefit Clause', 'Coverage Limits', 'Benefit Type', 'Numeric Limit Amount'
    ];
    const rows = dataToExport.map((item: any, idx: number) => {
      const policyNo = item.policy_no || item.member_id || '—';
      const planName = item.plan_name || '—';
      const networkType = item.network_type || '—';
      const startDate = item.start_date ? new Date(item.start_date).toLocaleDateString('en-US') : '—';
      const endDate = item.end_date ? new Date(item.end_date).toLocaleDateString('en-US') : '—';
      const status = item.status || 'Active';
      const limitAmount = item.gross_claim_amount > 0 ? `$${item.gross_claim_amount.toLocaleString()}` : '—';
      
      return [
        idx + 1,
        `"${policyNo.replace(/"/g, '""')}"`,
        `"${planName.replace(/"/g, '""')}"`,
        `"${networkType.replace(/"/g, '""')}"`,
        `"${startDate.replace(/"/g, '""')}"`,
        `"${endDate.replace(/"/g, '""')}"`,
        `"${status.replace(/"/g, '""')}"`,
        `"${cleanSymbols(item.service_category || '').replace(/"/g, '""')}"`,
        `"${cleanSymbols(item.treating_physician || '').replace(/"/g, '""')}"`,
        `"${(item.service_type || '').replace(/"/g, '""')}"`,
        `"${limitAmount.replace(/"/g, '""')}"`
      ];
    });
    
    const csvContent = [headers.join(','), ...rows.map((r: any[]) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const planNameClean = (selectedPlan || 'policy').replace(/[^a-zA-Z0-9]/g, '_');
    link.setAttribute('download', `${planNameClean}_benefits_export.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportPolicyExcel = () => {
    const dataToExport = hasPolicies ? (policyDetails?.benefits || []) : (claimsData?.results || []);
    if (!dataToExport.length) return;
    
    let html = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta http-equiv="content-type" content="text/plain; charset=UTF-8"/>
        <!--[if gte mso 9]>
        <xml>
          <x:ExcelWorkbook>
            <x:ExcelWorksheets>
              <x:ExcelWorksheet>
                <x:Name>Policy Benefits</x:Name>
                <x:WorksheetOptions>
                  <x:DisplayGridlines/>
                </x:WorksheetOptions>
              </x:ExcelWorksheet>
            </x:ExcelWorksheets>
          </x:ExcelWorkbook>
        </xml>
        <![endif]-->
        <style>
          th { background-color: #f1f5f9; font-weight: bold; border: 1px solid #cbd5e1; text-align: left; }
          td { border: 1px solid #cbd5e1; }
        </style>
      </head>
      <body>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Policy Number</th>
              <th>Plan Name</th>
              <th>Network Type</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Status</th>
              <th>Policy Benefit Clause</th>
              <th>Coverage Limits</th>
              <th>Benefit Type</th>
              <th>Numeric Limit Amount</th>
            </tr>
          </thead>
          <tbody>
    `;
    
    dataToExport.forEach((item: any, idx: number) => {
      const policyNo = item.policy_no || item.member_id || '—';
      const planName = item.plan_name || '—';
      const networkType = item.network_type || '—';
      const startDate = item.start_date ? new Date(item.start_date).toLocaleDateString('en-US') : '—';
      const endDate = item.end_date ? new Date(item.end_date).toLocaleDateString('en-US') : '—';
      const status = item.status || 'Active';
      const limitAmount = item.gross_claim_amount > 0 ? `$${item.gross_claim_amount.toLocaleString()}` : '—';

      html += `
        <tr>
          <td>${idx + 1}</td>
          <td>${policyNo}</td>
          <td>${planName}</td>
          <td>${networkType}</td>
          <td>${startDate}</td>
          <td>${endDate}</td>
          <td>${status}</td>
          <td>${cleanSymbols(item.service_category || '')}</td>
          <td>${cleanSymbols(item.treating_physician || '')}</td>
          <td>${item.service_type || ''}</td>
          <td>${limitAmount}</td>
        </tr>
      `;
    });
    
    html += `
          </tbody>
        </table>
      </body>
      </html>
    `;
    
    const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const planNameClean = (selectedPlan || 'policy').replace(/[^a-zA-Z0-9]/g, '_');
    link.setAttribute('download', `${planNameClean}_benefits_export.xls`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchAllProvidersForExport = async (): Promise<ProviderRow[]> => {
    setIsExporting(true);
    try {
      const backendSortMap: Record<string, string> = {
        provider_name: 'name',
        visits: 'claims',
        total_net: 'totalAmount',
        net_share: 'percentage'
      };
      const backendSort = backendSortMap[sortField] || 'claims';
      const q = new URLSearchParams({
        page: '1',
        pageSize: '10000', // Retrieve all records
        sort: backendSort,
        order: sortOrder,
        ...(search && { search }),
      });
      const res = await fetch(`${API_BASE_URL}/api/dashboard/providers?${q}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`Error ${res.status}`);
      const data = await res.json();
      return (data.providers || []).map((p: any) => ({
        provider_name: p.name,
        visits: p.claims,
        total_net: p.totalAmount,
        net_share: p.percentage / 100
      }));
    } catch (err) {
      console.error('Export fetch failed:', err);
      return tableRows;
    } finally {
      setIsExporting(false);
    }
  };

  const exportClaimsCSV = async () => {
    const dataToExport = await fetchAllProvidersForExport();
    if (!dataToExport.length) return;
    
    const headers = ['#', 'Provider Name', 'Visits', 'Total Net', 'Net Share %'];
    const rows = dataToExport.map((row, idx) => [
      idx + 1,
      `"${(row.provider_name || row.provider || '-').replace(/"/g, '""')}"`,
      row.visits,
      row.total_net,
      `${(row.net_share * 100).toFixed(1)}%`
    ]);
    
    const csvContent = [headers.join(','), ...rows.map((r: any[]) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `provider_claims_export.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportClaimsExcel = async () => {
    const dataToExport = await fetchAllProvidersForExport();
    if (!dataToExport.length) return;
    
    let html = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <meta http-equiv="content-type" content="text/plain; charset=UTF-8"/>
        <!--[if gte mso 9]>
        <xml>
          <x:ExcelWorkbook>
            <x:ExcelWorksheets>
              <x:ExcelWorksheet>
                <x:Name>Providers Claims</x:Name>
                <x:WorksheetOptions>
                  <x:DisplayGridlines/>
                </x:WorksheetOptions>
              </x:ExcelWorksheet>
            </x:ExcelWorksheets>
          </x:ExcelWorkbook>
        </xml>
        <![endif]-->
        <style>
          th { background-color: #f1f5f9; font-weight: bold; border: 1px solid #cbd5e1; text-align: left; }
          td { border: 1px solid #cbd5e1; }
        </style>
      </head>
      <body>
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Provider Name</th>
              <th>Visits</th>
              <th>Total Net</th>
              <th>Net Share %</th>
            </tr>
          </thead>
          <tbody>
    `;
    
    dataToExport.forEach((row, idx) => {
      html += `
        <tr>
          <td>${idx + 1}</td>
          <td>${row.provider_name || row.provider || '-'}</td>
          <td>${row.visits}</td>
          <td>${row.total_net}</td>
          <td>${(row.net_share * 100).toFixed(1)}%</td>
        </tr>
      `;
    });
    
    html += `
          </tbody>
        </table>
      </body>
      </html>
    `;
    
    const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `provider_claims_export.xls`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  useEffect(() => {
    setBenefitPage(1);
  }, [filterType, searchInput, selectedPlan]);
  const searchTimer                   = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sortField = sorting[0]?.id   ?? 'visits';
  const sortOrder = sorting[0]?.desc ? 'desc' : 'asc';

  // ── TanStack Query — provider table ──────────────────────────────────────
  const {
    data: tableData,
    isFetching: tableFetching,
    isError: tableError,
    error: tableErr,
    refetch: tableRefetch,
  } = useQuery({
    queryKey: ['provider-table', tablePage, tablePageSize, sortField, sortOrder, search],
    queryFn: () => fetchProviderTable({
      page: tablePage, pageSize: tablePageSize,
      sort: sortField, order: sortOrder, search,
    }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const tableRows      = tableData?.providers    ?? [];
  const tablePagination = tableData?.pagination;
  const totalPages     = tablePagination?.total_pages ?? 1;

  // ── TanStack Table ───────────────────────────────────────────────────────
  const table = useReactTable({
    data: tableRows,
    columns: providerColumns,
    state: { sorting },
    onSortingChange: (updater) => {
      setSorting(updater);
      setTablePage(1);
    },
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
  });

  // ── Search debounce 400ms ────────────────────────────────────────────────
  const handleSearch = useCallback((val: string) => {
    setSearchInput(val);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearch(val);
      setTablePage(1);
    }, 400);
  }, []);

  // ── Derived chart data ───────────────────────────────────────────────────
  const volume     = kpis?.volume           || {};
  const ioData     = kpis?.io_distribution  || {};
  const avgCost    = kpis?.avg_cost_per_visit?.overall || {};
  const losData    = kpis?.inpatient_los    || {};
  const cptData    = kpis?.cpt_kpis?.top_by_lines?.slice(0, 8)       || [];
  const icdData    = kpis?.diagnosis_kpis?.top_by_visits?.slice(0, 8) || [];
  const financials = kpis?.financial_ratios || {};
  const highCost   = kpis?.high_cost_visits || {};

  const ioCounts   = ioData.counts || {};
  const totalIO    = Object.values(ioCounts).reduce((a: any, b: any) => a + b, 0) as number;
  const ioPieData  = Object.entries(ioCounts).map(([key, val]) => ({
    name: key === 'I' ? 'Inpatient' : 'Outpatient',
    value: val as number,
    color: key === 'I' ? IO_COLORS.I : IO_COLORS.O,
  }));
  const byIO       = kpis?.avg_cost_per_visit?.by_io || {};
  const ioCostData = Object.entries(byIO).map(([key, val]: [string, any]) => ({
    name: key === 'I' ? 'Inpatient' : 'Outpatient',
    avg: Math.round(val.avg),
    visits: val.visits,
  }));

  // ─────────────────────────────────────────────────────────────────────────
  if (kpiLoading && !kpis) return <Loading />;
  if (kpiError && !kpis) return (
    <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', padding: '16px 18px', margin: '24px', display: 'flex', gap: '10px', alignItems: 'center' }}>
      <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#ef4444" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <p style={{ fontSize: '13px', color: '#dc2626', margin: 0 }}>{(kpiErr as Error)?.message}</p>
    </div>
  );
  // Only block on EmptyState for the claims view — policy view has its own data source
  if (!isPolicyBenefits && (!kpis || !kpis.volume)) return <EmptyState />;
  if (isPolicyBenefits && loadingDetails && !policyDetails) return <Loading />;

  if (isPolicyBenefits) {
    const claims = hasPolicies ? (policyDetails?.benefits || []) : (claimsData?.results || []);
    const policy = hasPolicies
      ? (policyDetails?.policy || {})
      : {
          insurance_company: claims[0]?.provider_name || 'QLM LIFE & MEDICAL INSURANCE',
          insured_name: claims[0]?.payer_name || 'AL FARDAN GROUP',
          policy_no: claims[0]?.member_id || 'P2209000088-R3',
          network_type: 'PRIVILEGE',
          start_date: claims[0]?.service_date || '2025-07-31',
          end_date: '2026-07-30'
        };

    // Filter claims by service type and search query
    const filteredClaims = claims.filter((c: any) => {
      const matchSearch = (c.service_category || '').toLowerCase().includes(searchInput.toLowerCase()) ||
                          (c.treating_physician || '').toLowerCase().includes(searchInput.toLowerCase()) ||
                          (c.plan_name || '').toLowerCase().includes(searchInput.toLowerCase()) ||
                          (c.network_type || '').toLowerCase().includes(searchInput.toLowerCase());
      if (filterType === 'inpatient') {
        return matchSearch && c.service_type === 'Inpatient';
      }
      if (filterType === 'outpatient') {
        return matchSearch && c.service_type === 'Outpatient';
      }
      return matchSearch;
    });

    const inpatientCount = claims.filter((c: any) => c.service_type === 'Inpatient').length;
    const outpatientCount = claims.filter((c: any) => c.service_type === 'Outpatient').length;
    const cappedBenefits = claims.filter((c: any) => (c.gross_claim_amount || 0) > 0 || c.treating_physician?.includes('NIL') || c.treating_physician?.includes('QAR') || c.treating_physician?.includes('QR')).length;

    // Dynamically retrieve overall annual limit if it exists in the benefits clause
    const limitRow = claims.find((b: any) => b.service_category?.toUpperCase()?.includes('OVERALL ANNUAL LIMIT'));
    const limitText = selectedPlan === 'ALL_POLICIES'
      ? 'Various Limits'
      : limitRow
        ? cleanSymbols(limitRow.treating_physician || '').replace(/QAR\s*/gi, '$')
        : '$350,000';

    const startDateStr = policy.start_date ? new Date(policy.start_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'July 31, 2025';
    const endDateStr = policy.end_date ? new Date(policy.end_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'July 30, 2026';

    // Pagination calculations
    const itemsPerPage = 10;
    const totalItems = filteredClaims.length;
    const totalBenefitPages = Math.ceil(totalItems / itemsPerPage) || 1;
    const startIndex = (benefitPage - 1) * itemsPerPage;
    const paginatedClaims = filteredClaims.slice(startIndex, startIndex + itemsPerPage);

    return (
      <div style={{ padding: '28px', maxWidth: '1300px', margin: '0 auto', fontFamily: "'Inter', -apple-system, sans-serif" }}>

        {/* Banner metadata */}
        <div style={{
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          borderRadius: '24px',
          padding: '32px',
          color: '#fff',
          boxShadow: '0 20px 40px -15px rgba(15, 23, 42, 0.3)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          marginBottom: '28px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative shapes */}
          <div style={{ position: 'absolute', top: '-10%', right: '-5%', width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(99,102,241,0.1) 0%, transparent 70%)', borderRadius: '50%' }} />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px', position: 'relative', zIndex: 1 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <span style={{
                  background: 'rgba(99, 102, 241, 0.15)',
                  color: '#c7d2fe',
                  border: '1px solid rgba(99, 102, 241, 0.3)',
                  fontSize: '11px',
                  fontWeight: 700,
                  padding: '5px 14px',
                  borderRadius: '999px',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#6366f1', display: 'inline-block' }}></span>
                  OCR Policy Ingested
                </span>
              </div>
              <h1 style={{ fontSize: '32px', fontWeight: 800, margin: '6px 0 6px', letterSpacing: '-0.5px', color: '#f8fafc' }}>
                {policy.insurance_company || kpis?.claims_per_provider?.[0]?.provider_name || 'QLM LIFE & MEDICAL INSURANCE'}
              </h1>
              <p style={{ fontSize: '15px', color: '#94a3b8', margin: 0, fontWeight: 500 }}>
                Group Health Benefits Framework for <b style={{ color: '#f1f5f9' }}>{policy.insured_name || 'AL FARDAN GROUP'}</b>
              </p>
            </div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.03)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              padding: '20px 24px',
              borderRadius: '20px',
              textAlign: 'right',
              boxShadow: 'inset 0 1px 1px rgba(255, 255, 255, 0.05)',
              minWidth: '220px'
            }}>
              <span style={{ fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 700 }}>Overall Annual Limit</span>
              <p style={{ fontSize: '28px', fontWeight: 800, color: '#38bdf8', margin: '6px 0 2px', letterSpacing: '-0.5px' }}>{limitText}</p>
              <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 500 }}>Per Person / Policy Year</span>
            </div>
          </div>

          <div style={{
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
            marginTop: '28px',
            paddingTop: '24px',
            display: 'flex',
            gap: '40px',
            flexWrap: 'wrap',
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255, 255, 255, 0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#94a3b8" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <span style={{ fontSize: '10px', color: '#94a3b8', letterSpacing: '0.5px', textTransform: 'uppercase', fontWeight: 700 }}>Policy Identifier</span>
                <p style={{ fontSize: '14px', fontWeight: 600, color: '#f1f5f9', margin: '2px 0 0' }}>{policy.policy_no || 'P2209000088-R3'}</p>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(255, 255, 255, 0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255, 255, 255, 0.06)' }}>
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#94a3b8" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <span style={{ fontSize: '10px', color: '#94a3b8', letterSpacing: '0.5px', textTransform: 'uppercase', fontWeight: 700 }}>Insurance Period</span>
                <p style={{ fontSize: '14px', fontWeight: 600, color: '#f1f5f9', margin: '2px 0 0' }}>{startDateStr} – {endDateStr}</p>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(52, 211, 153, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(52, 211, 153, 0.2)' }}>
                <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#34d399" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <div>
                <span style={{ fontSize: '10px', color: '#34d399', letterSpacing: '0.5px', textTransform: 'uppercase', fontWeight: 700 }}>Network Category</span>
                <p style={{ fontSize: '14px', fontWeight: 600, color: '#34d399', margin: '2px 0 0' }}>{policy.network_type || 'PRIVILEGE'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Highlight Metrics */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '28px' }}>
          <KPICard label="Total Benefits Clauses" value={formatNumber(claims.length)} sub="Mapped policy sections"
            color="#6366f1" bg="#eef2ff" gradient="linear-gradient(135deg, #6366f1, #818cf8)" glow="rgba(99,102,241,0.2)"
            iconPath="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          <KPICard label="Inpatient & Day-Care" value={formatNumber(inpatientCount)} sub="Private accommodation & ICU"
            color="#0ea5e9" bg="#eff6ff" gradient="linear-gradient(135deg, #0ea5e9, #38bdf8)" glow="rgba(14,165,233,0.2)"
            iconPath="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          <KPICard label="Outpatient & Clinical" value={formatNumber(outpatientCount)} sub="Consultations & Vaccinations"
            color="#22c55e" bg="#f0fdf4" gradient="linear-gradient(135deg, #22c55e, #4ade80)" glow="rgba(34,197,94,0.2)"
            iconPath="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          <KPICard label="Custom Limits & Caps" value={formatNumber(cappedBenefits)} sub="Specific monetary restrictions"
            color="#f59e0b" bg="#fffbeb" gradient="linear-gradient(135deg, #f59e0b, #fbbf24)" glow="rgba(245,158,11,0.2)"
            iconPath="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </div>

        {/* Charts Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '28px' }}>
          {/* Chart 1: Benefit Type Breakdown */}
          <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <div style={{ padding: '18px 20px', background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '32px', height: '32px', background: 'linear-gradient(135deg, #6366f1, #818cf8)', borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" /></svg>
              </div>
              <div>
                <p style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', margin: 0 }}>Benefit Type Breakdown</p>
                <p style={{ fontSize: '11px', color: '#64748b', margin: 0 }}>Inpatient vs Outpatient Clauses</p>
              </div>
            </div>
            <div style={{ padding: '20px', height: '280px' }}>
              {inpatientCount > 0 || outpatientCount > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Inpatient', value: inpatientCount },
                        { name: 'Outpatient', value: outpatientCount }
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      dataKey="value"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                      labelLine={false}
                    >
                      <Cell fill="#6366f1" />
                      <Cell fill="#22c55e" />
                    </Pie>
                    <Tooltip formatter={(v: any) => `${v} clauses`} wrapperStyle={{ pointerEvents: 'none' }} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: '13px' }}>No benefit type data</div>}
            </div>
          </div>

          {/* Chart 2: Top Benefit Categories */}
          <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
            <div style={{ padding: '18px 20px', background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '32px', height: '32px', background: 'linear-gradient(135deg, #22c55e, #4ade80)', borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
              </div>
              <div>
                <p style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', margin: 0 }}>Top Benefit Categories</p>
                <p style={{ fontSize: '11px', color: '#64748b', margin: 0 }}>Distribution of clauses across categories</p>
              </div>
            </div>
            <div style={{ padding: '20px', height: '280px' }}>
              {(() => {
                const categoryCounts: Record<string, number> = {};
                claims.forEach((c: any) => {
                  const cat = cleanSymbols(c.service_category || 'Other');
                  if (cat) {
                    categoryCounts[cat] = (categoryCounts[cat] || 0) + 1;
                  }
                });
                const topCategories = Object.entries(categoryCounts)
                  .map(([name, value]) => ({ name, value }))
                  .sort((a, b) => b.value - a.value)
                  .slice(0, 5);

                return topCategories.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topCategories} layout="vertical" margin={{ top: 5, right: 30, left: 40, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v: any) => `${v} clauses`} wrapperStyle={{ pointerEvents: 'none' }} />
                      <Bar dataKey="value" name="Clauses" fill="#0ea5e9" radius={[0, 6, 6, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: '13px' }}>No categories data</div>;
              })()}
            </div>
          </div>
        </div>

        {/* Structured table layout */}
        <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
          {/* Read-Only Contract Schedule Banner */}
          <div style={{
            padding: '12px 20px',
            background: 'linear-gradient(to right, #f0fdfa, #f0f9ff)',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#0f766e' }}>
              <Lock style={{ width: '15px', height: '15px', color: '#0d9488', flexShrink: 0 }} />
              <span><b>Official Contract Schedule (Read-Only Baseline):</b> These policy clauses and limits are automatically parsed via OCR from your policy booklet. Click any clause row or &quot;Inspect&quot; to view detailed terms, caps, and rules.</span>
            </div>
            <div style={{ fontSize: '11px', fontWeight: 700, color: '#0284c7', background: '#e0f2fe', padding: '4px 10px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span>Protected Contract Rules</span>
            </div>
          </div>

          {/* Header section with Tabs and search */}
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
            {/* Filter buttons */}
            <div style={{ display: 'flex', background: '#f1f5f9', padding: '4px', borderRadius: '10px', gap: '2px' }}>
              {(['all', 'inpatient', 'outpatient'] as const).map(tab => (
                <button
                  key={tab}
                  onClick={() => setFilterType(tab)}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '8px',
                    border: 'none',
                    fontSize: '13px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                    background: filterType === tab ? '#fff' : 'transparent',
                    color: filterType === tab ? '#1e1b4b' : '#64748b',
                    boxShadow: filterType === tab ? '0 2px 6px rgba(0,0,0,0.05)' : 'none'
                  }}
                >
                  {tab === 'all' ? 'All Benefits' : tab === 'inpatient' ? 'Inpatient & Day-Care' : 'Outpatient'}
                </button>
              ))}
            </div>

            {/* Export and Search */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                type="button"
                onClick={exportPolicyCSV}
                style={{
                  padding: '8px 14px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  background: '#fff',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#475569',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = '#f8fafc';
                  e.currentTarget.style.borderColor = '#cbd5e1';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = '#fff';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                }}
              >
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                CSV
              </button>
              <button
                type="button"
                onClick={exportPolicyExcel}
                style={{
                  padding: '8px 14px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  background: '#fff',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#475569',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = '#f8fafc';
                  e.currentTarget.style.borderColor = '#cbd5e1';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = '#fff';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                }}
              >
                <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Excel
              </button>

              {policiesList && policiesList.length > 0 && (
                <div style={{ position: 'relative', minWidth: '200px' }}>
                  <select
                    value={selectedPlan}
                    onChange={(e) => setSelectedPlan(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 30px 8px 12px',
                      borderRadius: '8px',
                      border: '1px solid #e2e8f0',
                      background: '#fff',
                      fontSize: '13px',
                      fontWeight: 600,
                      color: '#475569',
                      outline: 'none',
                      cursor: 'pointer',
                      appearance: 'none',
                      boxSizing: 'border-box',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.background = '#f8fafc';
                      e.currentTarget.style.borderColor = '#cbd5e1';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.background = '#fff';
                      e.currentTarget.style.borderColor = '#e2e8f0';
                    }}
                  >
                    {policiesList.length > 1 && (
                      <option value="ALL_POLICIES">All Uploaded Policies</option>
                    )}
                    {policiesList.map((p: any) => (
                      <option key={p.plan_name} value={p.plan_name}>
                        {p.insured_name ? `${p.insured_name} - ` : ''}Plan: {p.plan_name}
                      </option>
                    ))}
                  </select>
                  <svg
                    style={{
                      position: 'absolute',
                      right: '10px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#94a3b8',
                      pointerEvents: 'none'
                    }}
                    width="14"
                    height="14"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              )}

              <div style={{ position: 'relative', minWidth: '260px' }}>
                <svg style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search benefit details or limits..."
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  style={{ width: '100%', paddingLeft: '32px', paddingRight: '12px', paddingTop: '8px', paddingBottom: '8px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', color: '#374151', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
            </div>
          </div>

          {/* Table content */}
          <div style={{ overflowX: 'auto', opacity: fetchingDetails ? 0.6 : 1, transition: 'opacity 0.15s' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={{ padding: '14px 16px', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left', width: '40px' }}>#</th>
                  <th style={{ padding: '14px 16px', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left' }}>Policy Benefit Clause</th>
                  <th style={{ padding: '14px 16px', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left', width: '220px' }}>Coverage Limits</th>
                  <th style={{ padding: '14px 16px', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'center', width: '120px' }}>Benefit Type</th>
                  <th style={{ padding: '14px 16px', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'right' }}>Numeric Limit</th>
                  <th style={{ padding: '14px 16px', fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'center', width: '100px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {paginatedClaims.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ padding: '48px', textAlign: 'center', color: '#94a3b8' }}>
                      No policy benefits found matching your search term.
                    </td>
                  </tr>
                ) : (
                  paginatedClaims.map((item: any, idx: number) => {
                    const cleanedCategory = cleanSymbols(item.service_category || '');
                    const cleanedPhysician = cleanSymbols(item.treating_physician || '').replace(/QAR\s*/gi, '$').replace(/QR\s*/gi, '$');
                    const isCoveredOnly = cleanedPhysician.toLowerCase() === 'covered';
                    const hasLimitAmount = (item.gross_claim_amount || 0) > 0;
                    
                    const policyNo = item.policy_no || item.member_id || '—';
                    const planName = item.plan_name || '—';
                    const networkType = item.network_type || '—';

                    return (
                      <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s', cursor: 'pointer' }}
                        onClick={() => setSelectedClause(item)}
                        onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>

                        <td style={{ padding: '14px 16px', color: '#94a3b8', fontWeight: 600 }}>{startIndex + idx + 1}</td>
                        
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ color: item.service_type === 'Inpatient' ? '#6366f1' : '#10b981', fontSize: '12px' }}>▪</span>
                            <span style={{ fontWeight: 600, color: '#0f172a' }}>{toTitleCase(cleanedCategory)}</span>
                          </div>
                          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px', paddingLeft: '14px', flexWrap: 'wrap' }}>
                            <span>Plan: <b style={{ color: '#334155', fontWeight: 600 }}>{toTitleCase(planName)}</b></span>
                            <span style={{ color: '#cbd5e1' }}>•</span>
                            <span>Policy: <b style={{ color: '#334155', fontWeight: 600, fontFamily: 'monospace' }}>{policyNo}</b></span>
                            <span style={{ color: '#cbd5e1' }}>•</span>
                            <span>Network: <b style={{ color: '#0284c7', fontWeight: 700 }}>{toTitleCase(networkType)}</b></span>
                          </div>
                        </td>

                        <td style={{ padding: '14px 16px', textAlign: 'left' }}>
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '999px',
                            fontSize: '12px',
                            fontWeight: 700,
                            display: 'inline-block',
                            background: isCoveredOnly
                              ? '#f0fdf4'
                              : hasLimitAmount
                                ? '#eff6ff'
                                : '#f8fafc',
                            color: isCoveredOnly
                              ? '#15803d'
                              : hasLimitAmount
                                ? '#1d4ed8'
                                : '#475569',
                            border: isCoveredOnly
                              ? '1px solid #bbf7d0'
                              : hasLimitAmount
                                ? '1px solid #bfdbfe'
                                : '1px solid #e2e8f0'
                          }}>
                            {toTitleCase(cleanedPhysician)}
                          </span>
                        </td>

                        <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                          <span style={{
                            padding: '4px 10px',
                            borderRadius: '999px',
                            fontSize: '11px',
                            fontWeight: 700,
                            background: item.service_type === 'Inpatient' ? '#eef2ff' : '#ecfdf5',
                            color: item.service_type === 'Inpatient' ? '#4f46e5' : '#059669',
                            border: item.service_type === 'Inpatient' ? '1px solid #c7d2fe' : '1px solid #a7f3d0'
                          }}>
                            {item.service_type === 'Inpatient' ? 'Inpatient' : 'Outpatient'}
                          </span>
                        </td>

                        <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                          <span style={{ fontWeight: 700, color: hasLimitAmount ? '#0f766e' : '#64748b' }}>
                            {hasLimitAmount ? `$${item.gross_claim_amount.toLocaleString()}` : '—'}
                          </span>
                        </td>

                        <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedClause(item);
                            }}
                            style={{
                              padding: '6px 12px',
                              borderRadius: '10px',
                              border: '1px solid #e2e8f0',
                              background: '#ffffff',
                              color: '#334155',
                              fontSize: '11px',
                              fontWeight: 700,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '5px',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
                              transition: 'all 0.15s ease'
                            }}
                            onMouseEnter={e => {
                              e.currentTarget.style.background = '#0284c7';
                              e.currentTarget.style.color = '#ffffff';
                              e.currentTarget.style.borderColor = '#0284c7';
                              e.currentTarget.style.boxShadow = '0 2px 6px rgba(2, 132, 199, 0.25)';
                            }}
                            onMouseLeave={e => {
                              e.currentTarget.style.background = '#ffffff';
                              e.currentTarget.style.color = '#334155';
                              e.currentTarget.style.borderColor = '#e2e8f0';
                              e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.03)';
                            }}
                          >
                            <Eye style={{ width: '13px', height: '13px' }} />
                            Inspect
                          </button>
                        </td>

                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination footer */}
          <div style={{ padding: '16px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', flexWrap: 'wrap', gap: '12px' }}>
            <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
              Showing <b style={{ color: '#0f172a' }}>{totalItems > 0 ? startIndex + 1 : 0}</b> – <b style={{ color: '#0f172a' }}>{Math.min(benefitPage * itemsPerPage, totalItems)}</b> of <b style={{ color: '#0f172a' }}>{totalItems}</b> benefits
            </p>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              {/* First */}
              <button type="button" onClick={() => setBenefitPage(1)} disabled={benefitPage === 1}
                style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', fontSize: '12px', fontWeight: 600, color: benefitPage === 1 ? '#cbd5e1' : '#64748b', cursor: benefitPage === 1 ? 'not-allowed' : 'pointer' }}>«</button>
              {/* Prev */}
              <button type="button" onClick={() => setBenefitPage(p => Math.max(1, p - 1))} disabled={benefitPage === 1}
                style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', fontSize: '12px', fontWeight: 600, color: benefitPage === 1 ? '#cbd5e1' : '#64748b', cursor: benefitPage === 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg> Prev
              </button>
              {/* Page numbers */}
              {Array.from({ length: Math.min(5, totalBenefitPages) }, (_, i) => {
                const start = Math.max(1, Math.min(benefitPage - 2, totalBenefitPages - 4));
                const p = start + i;
                if (p < 1 || p > totalBenefitPages) return null;
                return (
                  <button type="button" key={p} onClick={() => setBenefitPage(p)}
                    style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px', fontWeight: 600, cursor: 'pointer', background: p === benefitPage ? '#6366f1' : '#fff', color: p === benefitPage ? 'white' : '#64748b' }}>
                    {p}
                  </button>
                );
              })}
              {/* Next */}
              <button type="button" onClick={() => setBenefitPage(p => Math.min(totalBenefitPages, p + 1))} disabled={benefitPage === totalBenefitPages}
                style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', fontSize: '12px', fontWeight: 600, color: benefitPage === totalBenefitPages ? '#cbd5e1' : '#64748b', cursor: benefitPage === totalBenefitPages ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                Next <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
              </button>
              {/* Last */}
              <button type="button" onClick={() => setBenefitPage(totalBenefitPages)} disabled={benefitPage === totalBenefitPages}
                style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', fontSize: '12px', fontWeight: 600, color: benefitPage === totalBenefitPages ? '#cbd5e1' : '#64748b', cursor: benefitPage === totalBenefitPages ? 'not-allowed' : 'pointer' }}>»</button>
            </div>
          </div>
        </div>

        {/* ── Policy Clause Detail Inspection Modal (uiuxpro design system) ── */}
        {selectedClause && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(15, 23, 42, 0.65)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              padding: '20px',
              animation: 'clauseModalFadeIn 0.2s cubic-bezier(0.16, 1, 0.3, 1)'
            }}
            onClick={() => setSelectedClause(null)}
          >
            <style>{`
              @keyframes clauseModalFadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              @keyframes clauseModalSlideUp {
                from { opacity: 0; transform: translateY(12px) scale(0.97); }
                to { opacity: 1; transform: translateY(0) scale(1); }
              }
            `}</style>
            <div
              style={{
                background: '#ffffff',
                borderRadius: '24px',
                width: '100%',
                maxWidth: '640px',
                boxShadow: '0 25px 50px -12px rgba(15, 23, 42, 0.35), 0 0 0 1px rgba(226, 232, 240, 0.8), 0 10px 20px -5px rgba(15, 23, 42, 0.1)',
                overflow: 'hidden',
                border: 'none',
                display: 'flex',
                flexDirection: 'column',
                animation: 'clauseModalSlideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div style={{
                padding: '22px 26px 18px',
                borderBottom: '1px solid #f1f5f9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <div style={{
                    width: '46px',
                    height: '46px',
                    borderRadius: '14px',
                    background: selectedClause.service_type === 'Inpatient'
                      ? 'linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%)'
                      : 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `1px solid ${selectedClause.service_type === 'Inpatient' ? '#c7d2fe' : '#a7f3d0'}`,
                    boxShadow: selectedClause.service_type === 'Inpatient'
                      ? '0 4px 12px rgba(99, 102, 241, 0.12)'
                      : '0 4px 12px rgba(16, 185, 129, 0.12)'
                  }}>
                    <FileText style={{
                      width: '22px',
                      height: '22px',
                      color: selectedClause.service_type === 'Inpatient' ? '#4f46e5' : '#059669'
                    }} />
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em' }}>
                        Policy Benefit Clause Details
                      </h3>
                      <span style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        color: '#0284c7',
                        background: '#e0f2fe',
                        padding: '2px 8px',
                        borderRadius: '9999px',
                        border: '1px solid #bae6fd',
                        letterSpacing: '0.02em'
                      }}>
                        Official Rule
                      </span>
                    </div>
                    <p style={{ margin: '3px 0 0', fontSize: '12px', color: '#64748b', fontWeight: 500 }}>
                      Official Schedule Rule & Audit Ground Truth
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedClause(null)}
                  style={{
                    background: '#f1f5f9',
                    border: 'none',
                    color: '#64748b',
                    cursor: 'pointer',
                    width: '34px',
                    height: '34px',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#e2e8f0';
                    e.currentTarget.style.color = '#0f172a';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f1f5f9';
                    e.currentTarget.style.color = '#64748b';
                  }}
                >
                  <X style={{ width: '18px', height: '18px' }} />
                </button>
              </div>

              {/* Modal Body */}
              <div style={{ padding: '24px 26px', display: 'flex', flexDirection: 'column', gap: '16px', maxHeight: '70vh', overflowY: 'auto' }}>
                
                {/* Hero Clause Bento Card */}
                <div style={{
                  padding: '18px 20px',
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                  border: '1px solid #e2e8f0',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.02)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Layers style={{ width: '14px', height: '14px', color: '#64748b' }} />
                      <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Benefit Clause / Category
                      </span>
                    </div>
                    <span style={{
                      fontSize: '11px',
                      fontWeight: 700,
                      padding: '3px 10px',
                      borderRadius: '9999px',
                      background: selectedClause.service_type === 'Inpatient' ? '#eef2ff' : '#ecfdf5',
                      color: selectedClause.service_type === 'Inpatient' ? '#4f46e5' : '#059669',
                      border: `1px solid ${selectedClause.service_type === 'Inpatient' ? '#c7d2fe' : '#a7f3d0'}`
                    }}>
                      {selectedClause.service_type || 'General'}
                    </span>
                  </div>
                  <h4 style={{
                    margin: 0,
                    fontSize: '16px',
                    fontWeight: 700,
                    color: '#0f172a',
                    lineHeight: 1.45,
                    letterSpacing: '-0.01em'
                  }}>
                    {toTitleCase(cleanSymbols(selectedClause.service_category || ''))}
                  </h4>
                </div>

                {/* Coverage & Limit Metrics */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  
                  {/* Coverage Specification */}
                  <div style={{
                    padding: '16px 18px',
                    borderRadius: '16px',
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 2px 6px rgba(15, 23, 42, 0.03)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                        <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#3b82f6' }} />
                        <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          Coverage Description
                        </span>
                      </div>
                      <p style={{
                        margin: 0,
                        fontSize: '15px',
                        fontWeight: 700,
                        color: '#0f172a',
                        lineHeight: 1.4
                      }}>
                        {toTitleCase(cleanSymbols(selectedClause.treating_physician || 'Covered').replace(/QAR\s*/gi, '$').replace(/QR\s*/gi, '$'))}
                      </p>
                    </div>
                    <div style={{ marginTop: '12px', fontSize: '11px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <CheckCircle2 style={{ width: '13px', height: '13px', color: '#10b981' }} />
                      <span style={{ fontWeight: 500 }}>Verified Schedule Rule</span>
                    </div>
                  </div>

                  {/* Monetary Limit Cap */}
                  <div style={{
                    padding: '16px 18px',
                    borderRadius: '16px',
                    background: selectedClause.gross_claim_amount > 0
                      ? 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)'
                      : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                    border: selectedClause.gross_claim_amount > 0 ? '1px solid #86efac' : '1px solid #e2e8f0',
                    boxShadow: '0 2px 6px rgba(15, 23, 42, 0.03)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                        <div style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          background: selectedClause.gross_claim_amount > 0 ? '#10b981' : '#94a3b8'
                        }} />
                        <span style={{
                          fontSize: '11px',
                          fontWeight: 700,
                          color: selectedClause.gross_claim_amount > 0 ? '#15803d' : '#64748b',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em'
                        }}>
                          Monetary Limit Cap
                        </span>
                      </div>
                      <p style={{
                        margin: 0,
                        fontSize: '17px',
                        fontWeight: 800,
                        color: selectedClause.gross_claim_amount > 0 ? '#14532d' : '#334155',
                        letterSpacing: '-0.02em'
                      }}>
                        {selectedClause.gross_claim_amount > 0
                          ? `$${selectedClause.gross_claim_amount.toLocaleString()}`
                          : 'Standard Covered'}
                      </p>
                    </div>
                    <div style={{
                      marginTop: '12px',
                      fontSize: '11px',
                      color: selectedClause.gross_claim_amount > 0 ? '#15803d' : '#94a3b8',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      <Shield style={{ width: '13px', height: '13px', color: selectedClause.gross_claim_amount > 0 ? '#16a34a' : '#94a3b8' }} />
                      <span style={{ fontWeight: 500 }}>
                        {selectedClause.gross_claim_amount > 0 ? 'Financial Hard Stop' : 'Standard Inclusions'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Contract Metadata 2x2 Bento */}
                <div style={{
                  borderRadius: '16px',
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  padding: '16px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
                    <Hash style={{ width: '13px', height: '13px', color: '#64748b' }} />
                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Contract & Policy Specifications
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div style={{ background: '#ffffff', padding: '12px 14px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                        Policy Plan
                      </span>
                      <p style={{ margin: '4px 0 0', fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>
                        {toTitleCase(selectedClause.plan_name || policy.plan_name || '—')}
                      </p>
                    </div>

                    <div style={{ background: '#ffffff', padding: '12px 14px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                        Policy Number
                      </span>
                      <p style={{ margin: '4px 0 0', fontSize: '13px', fontWeight: 700, color: '#0f172a', fontFamily: 'monospace' }}>
                        {selectedClause.policy_no || policy.policy_no || '—'}
                      </p>
                    </div>

                    <div style={{ background: '#ffffff', padding: '12px 14px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                        Network Tier
                      </span>
                      <p style={{ margin: '4px 0 0', fontSize: '13px', fontWeight: 700, color: '#0284c7' }}>
                        {toTitleCase(selectedClause.network_type || policy.network_type || '—')}
                      </p>
                    </div>

                    <div style={{ background: '#ffffff', padding: '12px 14px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                        Benefit Scope
                      </span>
                      <p style={{
                        margin: '4px 0 0',
                        fontSize: '13px',
                        fontWeight: 700,
                        color: selectedClause.service_type === 'Inpatient' ? '#4f46e5' : '#059669'
                      }}>
                        {selectedClause.service_type || 'General'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* AI Sentinel Protected Ground Truth Callout */}
                <div style={{
                  padding: '14px 16px',
                  borderRadius: '14px',
                  background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
                  border: '1px solid #a7f3d0',
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'center'
                }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    background: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #86efac',
                    flexShrink: 0,
                    boxShadow: '0 2px 4px rgba(16, 185, 129, 0.1)'
                  }}>
                    <ShieldCheck style={{ width: '20px', height: '20px', color: '#10b981' }} />
                  </div>
                  <div style={{ fontSize: '12px', color: '#166534', lineHeight: 1.5 }}>
                    <span style={{ fontWeight: 700, color: '#14532d' }}>Protected Ground Truth: </span>
                    This clause is parsed directly from your official schedule. It acts as an immutable baseline for the AI Fraud Sentinel to detect overbilling, non-covered services, and cap breaches.
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div style={{
                padding: '18px 26px',
                borderTop: '1px solid #f1f5f9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: '#f8fafc'
              }}>
                <button
                  type="button"
                  onClick={() => {
                    const textToCopy = `Clause: ${toTitleCase(cleanSymbols(selectedClause.service_category || ''))}\nCoverage: ${toTitleCase(cleanSymbols(selectedClause.treating_physician || ''))}\nLimit: ${selectedClause.gross_claim_amount > 0 ? '$' + selectedClause.gross_claim_amount.toLocaleString() : 'Covered'}\nPlan: ${toTitleCase(selectedClause.plan_name || policy.plan_name || 'N/A')}\nPolicy: ${selectedClause.policy_no || policy.policy_no || 'N/A'}`;
                    navigator.clipboard.writeText(textToCopy);
                    setCopiedClause(true);
                    setTimeout(() => setCopiedClause(false), 2000);
                  }}
                  style={{
                    padding: '9px 16px',
                    borderRadius: '11px',
                    border: '1px solid #e2e8f0',
                    background: '#ffffff',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: copiedClause ? '#16a34a' : '#475569',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '7px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f8fafc';
                    e.currentTarget.style.borderColor = '#cbd5e1';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#ffffff';
                    e.currentTarget.style.borderColor = '#e2e8f0';
                  }}
                >
                  {copiedClause ? <Check style={{ width: '15px', height: '15px', color: '#16a34a' }} /> : <Copy style={{ width: '15px', height: '15px' }} />}
                  {copiedClause ? 'Copied Details!' : 'Copy Clause Details'}
                </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setSearchInput(cleanSymbols(selectedClause.service_category || '').slice(0, 25));
                      setSelectedClause(null);
                    }}
                    style={{
                      padding: '9px 18px',
                      borderRadius: '11px',
                      border: '1px solid #0284c7',
                      background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
                      color: '#ffffff',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '7px',
                      boxShadow: '0 2px 6px rgba(2, 132, 199, 0.25)',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.filter = 'brightness(1.08)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.filter = 'brightness(1)';
                    }}
                  >
                    <Search style={{ width: '14px', height: '14px' }} />
                    Filter by this Clause
                  </button>

                  <button
                    type="button"
                    onClick={() => setSelectedClause(null)}
                    style={{
                      padding: '9px 20px',
                      borderRadius: '11px',
                      border: 'none',
                      background: '#0f172a',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#ffffff',
                      cursor: 'pointer',
                      boxShadow: '0 2px 4px rgba(15, 23, 42, 0.15)',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#1e293b';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#0f172a';
                    }}
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ padding: '28px', maxWidth: '1300px', margin: '0 auto' }}>

      {/* ── KPI Cards ────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '28px' }}>
        <KPICard label="Total Visits" value={formatNumber(volume.visits || 0)} sub={`${formatNumber(volume.claim_lines || 0)} claim lines`}
          color="#0ea5e9" bg="#eff6ff" gradient="linear-gradient(135deg, #0ea5e9, #38bdf8)" glow="rgba(14,165,233,0.2)"
          iconPath="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        <KPICard label="Inpatient Rate" value={`${((ioCounts['I'] || 0) / Math.max(totalIO, 1) * 100).toFixed(1)}%`} sub={`${formatNumber(ioCounts['I'] || 0)} inpatient visits`}
          color="#6366f1" bg="#eef2ff" gradient="linear-gradient(135deg, #6366f1, #818cf8)" glow="rgba(99,102,241,0.2)"
          iconPath="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
        <KPICard label="Avg Cost / Visit" value={formatCurrency(avgCost.avg || 0)} sub={`Median: ${formatCurrency(avgCost.median || 0)}`}
          color="#22c55e" bg="#f0fdf4" gradient="linear-gradient(135deg, #22c55e, #4ade80)" glow="rgba(34,197,94,0.2)"
          iconPath="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        <KPICard label="Avg LOS (Days)" value={losData.avg_los_days != null ? losData.avg_los_days.toFixed(1) : 'N/A'} sub={`${formatNumber(losData.inpatient_visits_with_los || 0)} inpatient visits`}
          color="#f59e0b" bg="#fffbeb" gradient="linear-gradient(135deg, #f59e0b, #fbbf24)" glow="rgba(245,158,11,0.2)"
          iconPath="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        <KPICard label="Unique Providers" value={formatNumber(volume.unique_providers || 0)} sub={`${formatNumber(volume.unique_members || 0)} unique members`}
          color="#a855f7" bg="#faf5ff" gradient="linear-gradient(135deg, #a855f7, #c084fc)" glow="rgba(168,85,247,0.2)"
          iconPath="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
        <KPICard label="High Cost Visits (P95)" value={formatCurrency(highCost.p95 || 0)} sub={`${formatNumber(highCost.visits_above_p95 || 0)} visits above P95`}
          color="#ef4444" bg="#fef2f2" gradient="linear-gradient(135deg, #ef4444, #f87171)" glow="rgba(239,68,68,0.2)"
          iconPath="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
      </div>

      {/* ── Charts Row 1: I/O ─────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <div style={{ padding: '18px 20px', background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '32px', height: '32px', background: 'linear-gradient(135deg, #6366f1, #818cf8)', borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" /></svg>
            </div>
            <div>
              <p style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', margin: 0 }}>Case Type Distribution</p>
              <p style={{ fontSize: '11px', color: '#64748b', margin: 0 }}>Inpatient vs Outpatient</p>
            </div>
          </div>
          <div style={{ padding: '20px', height: '280px' }}>
            {ioPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={ioPieData} cx="50%" cy="50%" outerRadius={100} dataKey="value"
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`} labelLine={false}>
                    {ioPieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip formatter={(v: any) => formatNumber(v)} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: '13px' }}>No I/O data</div>}
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <div style={{ padding: '18px 20px', background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '32px', height: '32px', background: 'linear-gradient(135deg, #22c55e, #4ade80)', borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <div>
              <p style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', margin: 0 }}>Avg Cost by Case Type</p>
              <p style={{ fontSize: '11px', color: '#64748b', margin: 0 }}>Inpatient vs Outpatient</p>
            </div>
          </div>
          <div style={{ padding: '20px', height: '280px' }}>
            {ioCostData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={ioCostData} margin={{ top: 10, right: 20, left: 20, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: any) => formatCurrency(v)} />
                  <Bar dataKey="avg" name="Avg Cost" radius={[8, 8, 0, 0]}>
                    {ioCostData.map((entry, i) => <Cell key={i} fill={entry.name === 'Inpatient' ? IO_COLORS.I : IO_COLORS.O} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: '13px' }}>No cost data</div>}
          </div>
        </div>
      </div>

      {/* ── Charts Row 2: CPT + ICD ───────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <div style={{ padding: '18px 20px', background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '32px', height: '32px', background: 'linear-gradient(135deg, #0ea5e9, #38bdf8)', borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
            </div>
            <div>
              <p style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', margin: 0 }}>Top CPT Codes</p>
              <p style={{ fontSize: '11px', color: '#64748b', margin: 0 }}>By claim lines</p>
            </div>
          </div>
          <div style={{ padding: '20px', height: '300px' }}>
            {cptData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={cptData} layout="vertical" margin={{ top: 5, right: 30, left: 60, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="cpt" width={55} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: any) => formatNumber(v)} />
                  <Bar dataKey="claim_lines" name="Claim Lines" fill={CPT_COLOR} radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: '13px' }}>No CPT data</div>}
          </div>
        </div>

        <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
          <div style={{ padding: '18px 20px', background: 'linear-gradient(135deg, #faf5ff, #ede9fe)', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '32px', height: '32px', background: 'linear-gradient(135deg, #a855f7, #c084fc)', borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            </div>
            <div>
              <p style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', margin: 0 }}>Top ICD Diagnoses</p>
              <p style={{ fontSize: '11px', color: '#64748b', margin: 0 }}>By visit count</p>
            </div>
          </div>
          <div style={{ padding: '20px', height: '300px' }}>
            {icdData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={icdData} layout="vertical" margin={{ top: 5, right: 30, left: 60, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="icd" width={55} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: any) => formatNumber(v)} />
                  <Bar dataKey="visits" name="Visits" fill={ICD_COLOR} radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', fontSize: '13px' }}>No ICD data</div>}
          </div>
        </div>
      </div>

      {/* ── Financial Ratios ─────────────────────────────────────────────── */}
      <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', marginBottom: '20px' }}>
        <div style={{ padding: '18px 20px', background: 'linear-gradient(135deg, #fff7ed, #ffedd5)', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '32px', height: '32px', background: 'linear-gradient(135deg, #f97316, #fb923c)', borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
          </div>
          <div>
            <p style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', margin: 0 }}>Financial Ratios</p>
            <p style={{ fontSize: '11px', color: '#64748b', margin: 0 }}>Discount, OOP & Net-to-Gross</p>
          </div>
        </div>
        <div style={{ padding: '20px', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
          {[
            { label: 'Avg Discount Rate', value: `${((financials.discount_rate?.mean || 0) * 100).toFixed(1)}%`, sub: `Median: ${((financials.discount_rate?.median || 0) * 100).toFixed(1)}%`, color: '#0ea5e9', bg: '#f0f9ff' },
            { label: 'Avg OOP Rate',      value: `${((financials.oop_rate?.mean    || 0) * 100).toFixed(1)}%`, sub: `Median: ${((financials.oop_rate?.median    || 0) * 100).toFixed(1)}%`, color: '#f97316', bg: '#fff7ed' },
            { label: 'Net-to-Gross',      value: `${((financials.net_to_gross?.mean|| 0) * 100).toFixed(1)}%`, sub: `Median: ${((financials.net_to_gross?.median|| 0) * 100).toFixed(1)}%`, color: '#22c55e', bg: '#f0fdf4' },
          ].map(item => (
            <div key={item.label} style={{ background: item.bg, borderRadius: '12px', padding: '16px', border: `1px solid ${item.color}25` }}>
              <p style={{ fontSize: '11px', color: '#64748b', margin: '0 0 6px', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600 }}>{item.label}</p>
              <p style={{ fontSize: '24px', fontWeight: 800, color: item.color, margin: '0 0 4px' }}>{item.value}</p>
              <p style={{ fontSize: '11px', color: '#94a3b8', margin: 0 }}>{item.sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          PROVIDER TABLE — TanStack Table + TanStack Query
          (search + sort + pagination — all server-side)
      ══════════════════════════════════════════════════════════════════════ */}
      <div style={{ background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>

        {/* Header + Search */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg, #6366f1, #818cf8)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(99,102,241,0.2)' }}>
              <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <div>
              <p style={{ fontSize: '15px', fontWeight: 800, color: '#0f172a', margin: 0 }}>Claims per Provider</p>
              <p style={{ fontSize: '12px', color: '#94a3b8', margin: 0, fontWeight: 500 }}>
                {tablePagination?.total_count?.toLocaleString() ?? '—'} providers
                {tableFetching && <span style={{ marginLeft: '8px', color: '#6366f1', fontSize: '11px' }}>Refreshing…</span>}
              </p>
            </div>
          </div>

          {/* Export + Search */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              type="button"
              onClick={exportClaimsCSV}
              disabled={isExporting}
              style={{
                padding: '8px 14px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                background: '#fff',
                fontSize: '13px',
                fontWeight: 600,
                color: '#475569',
                cursor: isExporting ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                opacity: isExporting ? 0.7 : 1,
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => {
                if (!isExporting) {
                  e.currentTarget.style.background = '#f8fafc';
                  e.currentTarget.style.borderColor = '#cbd5e1';
                }
              }}
              onMouseLeave={e => {
                if (!isExporting) {
                  e.currentTarget.style.background = '#fff';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                }
              }}
            >
              <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              CSV
            </button>
            <button
              type="button"
              onClick={exportClaimsExcel}
              disabled={isExporting}
              style={{
                padding: '8px 14px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                background: '#fff',
                fontSize: '13px',
                fontWeight: 600,
                color: '#475569',
                cursor: isExporting ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                opacity: isExporting ? 0.7 : 1,
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => {
                if (!isExporting) {
                  e.currentTarget.style.background = '#f8fafc';
                  e.currentTarget.style.borderColor = '#cbd5e1';
                }
              }}
              onMouseLeave={e => {
                if (!isExporting) {
                  e.currentTarget.style.background = '#fff';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                }
              }}
            >
              <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Excel
            </button>

            <div style={{ position: 'relative', minWidth: '220px' }}>
              <svg style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search provider..."
                value={searchInput}
                onChange={e => handleSearch(e.target.value)}
                style={{ width: '100%', paddingLeft: '32px', paddingRight: '12px', paddingTop: '8px', paddingBottom: '8px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', color: '#374151', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto', opacity: tableFetching ? 0.6 : 1, transition: 'opacity 0.2s' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              {table.getHeaderGroups().map(hg => (
                <tr key={hg.id} style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  {hg.headers.map(header => {
                    const canSort = header.column.getCanSort();
                    const sorted  = header.column.getIsSorted();
                    return (
                      <th key={header.id}
                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                        style={{
                          padding: '14px 16px', fontSize: '11px', fontWeight: 700, color: '#64748b',
                          textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap',
                          textAlign: header.id === 'rank' || header.id === 'provider_name' ? 'left' : 'right',
                          cursor: canSort ? 'pointer' : 'default',
                          userSelect: 'none',
                        }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort && <SortIcon direction={sorted} />}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {tableError ? (
                <tr><td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: '#ef4444', fontSize: '13px' }}>
                  {(tableErr as Error)?.message} — <button onClick={() => tableRefetch()} style={{ color: '#6366f1', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>Retry</button>
                </td></tr>
              ) : tableRows.length === 0 && !tableFetching ? (
                <tr><td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: '13px' }}>No providers found{search ? ` for "${search}"` : ''}</td></tr>
              ) : (
                table.getRowModel().rows.map(row => (
                  <tr key={row.id} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} style={{
                        padding: '12px 16px',
                        textAlign: cell.column.id === 'rank' || cell.column.id === 'provider_name' ? 'left' : 'right',
                      }}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        <div style={{ padding: '16px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8fafc', flexWrap: 'wrap', gap: '12px' }}>
          <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
            {tablePagination ? (
              <>Showing <b style={{ color: '#0f172a' }}>{((tablePage - 1) * tablePageSize) + 1}</b> – <b style={{ color: '#0f172a' }}>{Math.min(tablePage * tablePageSize, tablePagination.total_count)}</b> of <b style={{ color: '#0f172a' }}>{tablePagination.total_count.toLocaleString()}</b> providers</>
            ) : '—'}
          </p>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            {/* First */}
            <button onClick={() => setTablePage(1)} disabled={tablePage === 1 || tableFetching}
              style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', fontSize: '12px', fontWeight: 600, color: tablePage === 1 ? '#cbd5e1' : '#64748b', cursor: tablePage === 1 ? 'not-allowed' : 'pointer' }}>«</button>
            {/* Prev */}
            <button onClick={() => setTablePage(p => Math.max(1, p - 1))} disabled={tablePage === 1 || tableFetching}
              style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', fontSize: '12px', fontWeight: 600, color: tablePage === 1 ? '#cbd5e1' : '#64748b', cursor: tablePage === 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg> Prev
            </button>
            {/* Page numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const start = Math.max(1, Math.min(tablePage - 2, totalPages - 4));
              const p = start + i;
              return (
                <button key={p} onClick={() => setTablePage(p)} disabled={tableFetching}
                  style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px', fontWeight: 600, cursor: 'pointer', background: p === tablePage ? '#6366f1' : '#fff', color: p === tablePage ? 'white' : '#64748b' }}>
                  {p}
                </button>
              );
            })}
            {/* Next */}
            <button onClick={() => setTablePage(p => Math.min(totalPages, p + 1))} disabled={tablePage === totalPages || tableFetching}
              style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', fontSize: '12px', fontWeight: 600, color: tablePage === totalPages ? '#cbd5e1' : '#64748b', cursor: tablePage === totalPages ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
              Next <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
            </button>
            {/* Last */}
            <button onClick={() => setTablePage(totalPages)} disabled={tablePage === totalPages || tableFetching}
              style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #e2e8f0', background: '#fff', fontSize: '12px', fontWeight: 600, color: tablePage === totalPages ? '#cbd5e1' : '#64748b', cursor: tablePage === totalPages ? 'not-allowed' : 'pointer' }}>»</button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default CaseServiceDistribution;
